// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { trackCaptureEvent } from "@eisinc/App/CoveoConnector/Services/CoveoService";
import { getCoveoSettingsCollection } from "@eisinc/App/CoveoConnector/Store/Selectors/SettingsSelectors";
import { CaptureEventRequest } from "@eisinc/App/CoveoConnector/Types/schema";
import { addToEndOfChain, addToStartOfChain, ApiHandler } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { chain as AddCartLinesChain } from "@insite/client-framework/Store/Data/Carts/Handlers/AddCartLines";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWishListState } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { chain as AddLinesToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import { chain as AddToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { chain as AddWishListToCartChain } from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import { chain as UpdateCartLine } from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import { chain as ReorderOrderDetailsChain } from "@insite/client-framework/Store/Pages/OrderDetails/Handlers/Reorder";
import { chain as ReorderOrderHistoryChain } from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { chain as ReorderOrderStatusChain } from "@insite/client-framework/Store/Pages/OrderStatus/Handlers/Reorder";
import { chain as AddCartLineCollectionFromProductsChain } from "@insite/client-framework/Store/Pages/OrderUpload/Handlers/AddCartLineCollectionFromProducts";
import { chain as AddLinesToCartRequisitionsChain } from "@insite/client-framework/Store/Pages/Requisitions/Handlers/AddLinesToCart";
import { chain as PlaceOrderSavedOrderDetailsChain } from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/PlaceOrder";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";

type CartLineData = Pick<CartLineModel, "shortDescription" | "erpNumber" | "pricing" | "qtyOrdered" | "properties">;

type HandlerType = ApiHandler<
    {
        cartLineCollection?: {
            cartLines?: CartLineModel[];
        };
    },
    {
        cartLines?: CartLineModel[];
    } & CartLineData
>;

export const XngCoveoAddProductToCart: HandlerType = props => {
    const state = props.getState();
    const isConfiguredForCoveo = getCoveoSettingsCollection(state).coveoSettings.isConfiguredForCoveo;
    const session = getSession(props.getState());

    if (isConfiguredForCoveo) {
        if (!window) {
            return;
        }

        const cart = getCurrentCartState(props.getState()).value;
        let cartLinesToAdd: CartLineData[] = [];
        if (props.apiResult?.cartLines || props.apiParameter?.cartLineCollection?.cartLines) {
            // XNG-Changes: this if condition is for Order History Reorder functionality
            if ((props.apiResult?.cartLines as any)?.cartLines) {
                cartLinesToAdd = (props.apiResult?.cartLines as any)?.cartLines || [];
            } else {
                cartLinesToAdd = props.apiResult?.cartLines || props.apiParameter.cartLineCollection?.cartLines || [];
            }
        } else if (cart?.cartLines?.length) {
            cartLinesToAdd = cart.cartLines;
        } else {
            cartLinesToAdd = [
                {
                    shortDescription: props.apiResult?.shortDescription || "",
                    erpNumber: props.apiResult?.erpNumber || "",
                    pricing: props.apiResult?.pricing || null,
                    qtyOrdered: props.apiResult?.qtyOrdered || null,
                    properties: props.apiResult?.properties,
                },
            ];
        }

        const lastItemAdded = cartLinesToAdd?.[cartLinesToAdd?.length - 1];
        const targetProduct = lastItemAdded as any;
        const isVariantProduct = targetProduct.properties?.isVariantProduct?.toLowerCase() === "true";

        let eventName = "add";
        let cartLineParameter = (props.parameter as any).cartLine;
        if (cartLineParameter?.properties?.oldQty && cartLineParameter.qtyOrdered) {
            const oldQty = parseFloat(cartLineParameter.properties.oldQty);
            if (oldQty > cartLineParameter.qtyOrdered) {
                eventName = "remove";
            }

            const newProperties = cartLineParameter.properties;
            delete newProperties.oldQty;

            cartLineParameter = {
                ...cartLineParameter,
                properties: newProperties,
            };
        }

        const captureEventParameter: CaptureEventRequest = {
            pageUrl: window.location.href,
            eventName,
            searchHub: "listing",
            products: [{
                productId: isVariantProduct ? targetProduct.properties.parentProductId :targetProduct.productId,
                productName: isVariantProduct ? targetProduct.properties.parentProductShortDescription : targetProduct.shortDescription,
                price: targetProduct.pricing?.unitListPrice,
                brand: targetProduct.brand?.name as string,
                currencyCode: session.currency?.currencyCode,
                quantity: targetProduct.qtyOrdered,
                variantErpNumber: isVariantProduct ? targetProduct.erpNumber : null,
            }],
        };
        trackCaptureEvent(captureEventParameter);
    }
};

const _CoveoAddProductToCart = XngCoveoAddProductToCart as any;

addToEndOfChain(AddToCartChain, _CoveoAddProductToCart);
addToStartOfChain(UpdateCartLine, _CoveoAddProductToCart);
addToEndOfChain(AddWishListToCartChain, _CoveoAddProductToCart);
addToEndOfChain(AddCartLinesChain, _CoveoAddProductToCart);
addToEndOfChain(AddLinesToCartChain, _CoveoAddProductToCart);
addToEndOfChain(ReorderOrderDetailsChain, _CoveoAddProductToCart);
addToEndOfChain(ReorderOrderHistoryChain, _CoveoAddProductToCart);
addToEndOfChain(ReorderOrderStatusChain, _CoveoAddProductToCart);
addToEndOfChain(AddCartLineCollectionFromProductsChain, _CoveoAddProductToCart);
addToEndOfChain(AddLinesToCartRequisitionsChain, _CoveoAddProductToCart);
addToEndOfChain(PlaceOrderSavedOrderDetailsChain, _CoveoAddProductToCart);
